import React, { useState, useEffect } from "react";
import { PDFDocument } from "pdf-lib";
import { AutoComplete, Input, message, Progress, notification, Select } from 'antd';
import { Api } from "../../services";
import { uploadFileInChunks } from "../../services/chunkedUpload";
import { getLocalVideoDurationSec, getYoutubeDurationSec } from "../../services/localVideoDuration";
import { saveReplacedRecording } from "../../services/recordingStorage";
import { LeftOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import deleteIcon from "../../Assets/schedule/delete.svg";
import downArrowIcon from "../../Assets/schedule/downArrow.svg";
import upArrowIcon from "../../Assets/schedule/upArrow.svg";
import pdfIcon from "../../Assets/schedule/pdf.svg";
import videoIcon from "../../Assets/schedule/ytvideo.svg";
import magnifierIcon from "../../Assets/schedule/magnifier.svg";
import greenTickIcon from "../../Assets/courseManagementV4/green-check-icon.svg";
import SavingModal from "../../components/SavingModal";
import AulasSubscriptionModal from "pages/common/aulasSubsriptionModal";
import PageDataLoader from "components/pageDataLoader";
import ReactDOM from "react-dom";

const ResourceAddForm = ({
  onSave,
  onReset,
  subjectList = [],
  classList = [],
  chapterList = [],
  topicList = [],
  onFetchClasses,
  onFetchChapters,
  onFetchTopics,
  onSubjectChange,
  onClassChange,
  // redux upload actions (pass from parent)
  uploadBlock2,
  MergeChunk,
  uploadCompleted2,
  uploadlecturenotes,
  removemodel,
  postCreateTopic,
  // redux filter actions
  subjectFilter,
  fetchClasslist,
  fetchAdminChapterFilter,
  fetchFilterTopic,
  togglePageLoader,
  pageLoader,
  broadcastId,
  roomName,
  initialSubId,
  initialClassIds,
  onCancel,
  initialData = null,
  patchResourceTitle,
  BroadcastRecordingEdit,
  uploadRecordingChunk,
  mergeRecordingChunks,
  updateRecordingCount,
  deleteBroadcastRecording,
  deleteResourceContent,
  sizeOfContents
}) => {
  // A class recording has no chapter/topic of its own (it's the whole live
  // lesson's recording, not chapter-scoped content) - this form is shared
  // with regular resource upload/edit, so only suppress those fields for
  // this specific case rather than splitting into a separate component.
  // ResourcesPanel.js force-sets type="recording" on every class-recording
  // card (both the admin-library-backed and still-virtual broadcast ones),
  // so initialData.type is the reliable signal here.
  const isRecording = initialData?.type === 'recording';

  // Form state
  const [subjectId, setSubjectId] = useState(initialData?.subjects_id || initialData?.subjectId || initialSubId || null);
  const [subjectName, setSubjectName] = useState(() => {
    const val = initialData?.subjects_name || initialData?.subjectName || '';
    return typeof val === 'object' ? (val?.name || val?.title || '') : String(val);
  });
  const [selectedClasses, setSelectedClasses] = useState(initialData?.standards || initialClassIds || []);
  const [className, setClassName] = useState('');
  const [chapterId, setChapterId] = useState(initialData?.chapters_id || initialData?.chapterId || initialData?.chapter_id || null);
  const [chapterName, setChapterName] = useState(() => {
    const val = initialData?.chapters_name || initialData?.chapter_name || initialData?.chapterName || initialData?.chapter || '';
    return typeof val === 'object' ? (val?.name || val?.title || val?.chapterName || '') : String(val);
  });
  const [topicId, setTopicId] = useState(initialData?.topics_id || initialData?.topicId || initialData?.topic_id || null);
  const [topicName, setTopicName] = useState(() => {
    const val = initialData?.topics_name || initialData?.topic_name || initialData?.topicName || initialData?.topic || '';
    return typeof val === 'object' ? (val?.name || val?.title || val?.topicName || '') : String(val);
  });
  const [title, setTitle] = useState(initialData?.title || '');
  const [youtubeLink, setYoutubeLink] = useState(() => {
    const linkStr = initialData?.link || (typeof initialData?.file === 'string' ? initialData.file : '');
    const isYoutube = initialData?.type === 'youtube' || linkStr.includes('youtube.com') || linkStr.includes('youtu.be');
    return isYoutube ? linkStr : '';
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [pdfType, setPdfType] = useState(initialData?.type === 'lecture-notes' || initialData?.type === 'question-bank' || initialData?.type === 'pdf' ? (initialData.type === 'pdf' ? 'lecture-notes' : initialData.type) : null);
  const [showPdfTypeDropdown, setShowPdfTypeDropdown] = useState(false);
  // Storage-limit modal — same "not enough space" check/popup as the Course and Library
  // upload flows (videoResourcesContent.js / uploadModel.js), applied here for Schedule's
  // Resources tab.
  const [storageModalVisible, setStorageModalVisible] = useState(false);
  const [availableStorageBytes, setAvailableStorageBytes] = useState(null);

  // UI state
  const [showClassInput, setShowClassInput] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showChapterDropdown, setShowChapterDropdown] = useState(false);
  const [isChapterHovered, setIsChapterHovered] = useState(false);
  const [showTopicDropdown, setShowTopicDropdown] = useState(false);
  const [showTopicError, setShowTopicError] = useState(false);
  // null while the field is showing a value that arrived from an actual selection (or
  // is empty); the typed string while the user is searching. filterOption below needs
  // that distinction: re-opening the dropdown on an already-selected chapter must list
  // every chapter, but typing has to narrow the list so an unmatched search can fall
  // through to "No chapter found". The previous `inputValue === chapterName` guard
  // could not tell the two apart - onSearch writes the keystroke straight into
  // chapterName, so the guard was true on every keystroke and nothing ever filtered.
  const [chapterSearchText, setChapterSearchText] = useState(null);
  const [topicSearchText, setTopicSearchText] = useState(null);
  const [subjectDropdownOpen, setSubjectDropdownOpen] = useState(false);
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const [isYoutubeFocused, setIsYoutubeFocused] = useState(false);
  const [isYoutubeHovered, setIsYoutubeHovered] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [fileMetadata, setFileMetadata] = useState('');
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoDurationSec, setVideoDurationSec] = useState(0);
  const [dragType, setDragType] = useState(null);
  const [isInitialDataRemoved, setIsInitialDataRemoved] = useState(false);
  const isInitialYoutube = (() => {
    const linkStr = initialData?.link || (typeof initialData?.file === 'string' ? initialData.file : '');
    return initialData?.type === 'youtube' || linkStr.includes('youtube.com') || linkStr.includes('youtu.be');
  })();
  // The link this resource arrived with, so the save path can tell a genuine link
  // swap from an edit that only touched the title/chapter/topic. Both go through the
  // same `else if (youtubeLink)` branch, but only the swap needs a new row.
  const initialYoutubeUrl = isInitialYoutube
    ? (initialData?.link || (typeof initialData?.file === 'string' ? initialData.file : '') || '')
    : '';
  const hasLocalVideoMedia = !!selectedFile || (initialData && !isInitialYoutube && !isInitialDataRemoved);
  const hasYoutubeMedia = !!youtubeLink;

  // library-contents doesn't always echo back the videoDuration set at upload
  // time (same gap ResourcesPanel.js's ResourceItem works around) — when
  // that's missing here in the edit-mode file preview, derive it straight
  // from the actual file the same way, instead of just showing size alone.
  //
  // Kept in seconds (not pre-floored to whole minutes) for the same reason as
  // the upload-time fileMetadata fix above: initialData.videoDuration is
  // whatever whole-minutes value the backend has stored (that's all it was
  // ever sent - see videoDuration in handleSave's uploadObject), so it can
  // never show seconds. Recomputing straight from the actual video file gives
  // the real duration, so we now do that unconditionally for a video (not
  // only when the server value is missing) and prefer it for display.
  const [computedInitialDuration, setComputedInitialDuration] = useState(null);
  useEffect(() => {
    const nameForExtCheck = (initialData?.file || initialData?.title || '').toString().toLowerCase();
    const looksLikeVideo = /\.(mp4|avi|flv|mov|mkv|webm)$/.test(nameForExtCheck);
    const isVideoType = initialData && !isInitialYoutube && !isInitialDataRemoved && (initialData.type === 'video' || looksLikeVideo);
    if (!isVideoType) {
      setComputedInitialDuration(null);
      return;
    }
    const rawPath = initialData?.filePath || initialData?.file || initialData?.videoPath || initialData?.path || initialData?.s3Path || initialData?.fileKey || initialData?.key;
    if (!rawPath) return;
    const url = /^https?:\/\//i.test(rawPath) ? rawPath : `${Api.dlS3Url}${rawPath}`;
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      if (video.duration && isFinite(video.duration)) {
        setComputedInitialDuration(Math.round(video.duration));
      }
    };
    video.src = url;
    return () => { video.onloadedmetadata = null; video.src = ''; };
  }, [initialData, isInitialYoutube, isInitialDataRemoved]);
  // computedInitialDuration (seconds, from the real file) wins when available;
  // the server's whole-minutes value is only a fallback for when the file
  // itself can't be reached (converted to seconds so formatDuration below can
  // still render it consistently).
  const parseInitialDurationSec = (val) => {
    if (!val || val === 'N/A' || val === 0 || val === '0') return null;
    if (typeof val === 'string' && val.includes(':')) {
      const parts = val.split(':').map(Number);
      if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
      if (parts.length === 2) return parts[0] * 60 + parts[1];
    }
    const num = Number(val);
    if (!isNaN(num) && num > 0) {
      return Math.round(num > 10000 ? num / 1000 : (num > 120 ? num : num * 60));
    }
    return null;
  };
  const serverInitialSec = parseInitialDurationSec(initialData?.videoDuration || initialData?.duration || initialData?.fileDuration);
  const effectiveInitialDurationSeconds = computedInitialDuration != null
    ? computedInitialDuration
    : serverInitialSec;

  // Options for autocomplete
  const [optionSubject, setOptionsSubject] = useState([]);
  const [optionClass, setOptionsClass] = useState([]);
  const [optionChapter, setOptionsChapter] = useState([]);
  const [optionTopic, setOptionsTopic] = useState([]);

  // Custom Notification Helper (Top Right, One at a time)
  const showCustomNotification = (type, title, msg) => {
    notification.destroy(); // Limit to one visible snackbar
    const icon = type === 'error' ? (
      <div style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: '1.2px solid #D32F2F',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#D32F2F',
        fontSize: 10,
        fontWeight: 900,
        marginTop: 2
      }}>✕</div>
    ) : (
      <div style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: '1.2px solid #2E7D32',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#2E7D32',
        fontSize: 10,
        fontWeight: 900,
        marginTop: 2
      }}>✓</div>
    );

    notification.open({
      key: `notif-${Date.now()}`, // Ensure fresh notification every time
      message: <span style={{ fontSize: 16, fontWeight: 700, color: '#191919' }}>{title}</span>,
      description: <span style={{ fontSize: 14, color: '#636363', wordBreak: 'break-word' }}>{msg}</span>,
      icon: icon,
      placement: 'topRight',
      duration: 3,
      closeIcon: <span style={{ color: '#AEAEAE', fontSize: 16 }}>✕</span>,
      style: {
        borderRadius: 12,
        padding: '12px 16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        background: '#FFFFFF',
        width: 350,
        marginTop: 0
      }
    });
  };

  // Initial load of subjects
  useEffect(() => {
    if (subjectFilter) {
      if (initialData?.subjectName) {
        setSubjectName(initialData.subjectName);
      }
      subjectFilter(''); // Load all subjects initially
    }
  }, [initialData]);

  // Transform subject data to include proper id and name fields
  const getSubjectOptions = () => {
    if (!subjectList || subjectList.length === 0) return [];
    return subjectList.filter(item => item !== null && item !== undefined).map((item, index) => ({
      key: index,
      value: item.id || item.subjects_id || item.subjectId,
      label: item.name || item.subjects_name || item.subjectname
    }));
  };

  useEffect(() => {
    if (initialSubId && subjectList.length > 0) {
      const sub = subjectList.find(s => s && (s.id || s.subjects_id || s.subjectId) === initialSubId);
      if (sub) {
        setSubjectName(sub.name || sub.subjects_name || sub.subjectname);
        if (onFetchClasses) onFetchClasses(initialSubId);
        if (fetchClasslist) fetchClasslist(initialSubId, '');
      }
    }
  }, [initialSubId, subjectList]);

  useEffect(() => {
    setOptionsSubject(getSubjectOptions());
  }, [subjectList]);

  useEffect(() => {
    setOptionsClass((classList || []).filter(item => item !== null && item !== undefined).map((item, index) => ({
      key: index,
      label: item.std && item.section ? `${item.std}-${item.section}` : (item.standard ? item.standard + '-' + item.std_section : item.className),
      value: item.std && item.section ? `${item.std}-${item.section}` : (item.standard ? item.standard + '-' + item.std_section : item.className),
      classId: item.id || item.std_id || item.classid
    })));
  }, [classList]);

  useEffect(() => {
    if (chapterList && chapterList.length > 0) {
      setOptionsChapter((chapterList || []).filter(item => item !== null && item !== undefined).map((item, index) => {
        const id = item.id || item.chapterId || item.chapterid;
        return {
          key: index,
          label: item.name || item.chapterName,
          value: item.name || item.chapterName,
          chapterId: id,
          className: id === chapterId ? 'custom-selected-option' : ''
        };
      }));
    } else {
      setOptionsChapter([]);
    }
  }, [chapterList, chapterId]);

  useEffect(() => {
    if (topicList && topicList.length > 0) {
      setOptionsTopic((topicList || []).filter(item => item !== null && item !== undefined).map((item, index) => {
        const id = item.id || item.topicId;
        return {
          key: index,
          label: item.name || item.topicName,
          value: item.name || item.topicName,
          topicId: id,
          className: id === topicId ? 'custom-selected-option' : ''
        };
      }));
    } else {
      setOptionsTopic([]);
    }
  }, [topicList, topicId]);

  // If initialClassIds are provided, fetch chapters for the first class
  useEffect(() => {
    const sId = initialData?.subjects_id || initialData?.subjectId || initialSubId;
    const classes = initialData?.standards || initialClassIds;
    if (classes && classes.length > 0 && sId) {
      if (fetchAdminChapterFilter) {
        fetchAdminChapterFilter('', classes[0].id || classes[0].standardId, sId);
      }
    }
  }, [initialData, initialClassIds, initialSubId]);

  // If chapterId is present (Edit mode), fetch topics
  useEffect(() => {
    const sId = initialData?.subjects_id || initialData?.subjectId || initialSubId;
    const classes = initialData?.standards || initialClassIds;
    const cId = initialData?.chapters_id || initialData?.chapterId || initialData?.chapter_id || chapterId;

    if (classes && classes.length > 0 && sId && cId) {
      if (fetchFilterTopic) {
        fetchFilterTopic(sId, classes[0].id || classes[0].standardId, cId);
      }
    }
  }, [initialData, initialClassIds, initialSubId, chapterId]);

  // Subject handlers
  const handleSearchSubject = (value) => {
    if (!value) {
      setSubjectName('');
      setSubjectId(null);
    } else {
      setSubjectName(value);
      // Use the subjectFilter action from props
      if (subjectFilter) {
        subjectFilter(value);
      }
    }
  };

  const onSelectSubject = (value, option) => {
    if (value) {
      setSubjectId(value);
      setSubjectName(option.label);
      setSelectedClasses([]);
      setClassName('');
      setChapterId(null);
      setChapterName('');
      setTopicId(null);
      setTopicName('');
      setTitle('');
      // Notify parent about subject change
      if (onSubjectChange) {
        onSubjectChange(value);
      }
      // Fetch classes for selected subject
      if (onFetchClasses) {
        onFetchClasses(value);
      }
      // Also call fetchClasslist directly if available
      if (fetchClasslist) {
        fetchClasslist(value, '');
      }
    } else {
      setSubjectId(null);
      setSubjectName('');
    }
  };

  // Class handlers
  const handleSearchClass = (value) => {
    if (value) setClassName(value);
    else setClassName('');
  };

  const onSelectClass = (value, option) => {
    if (value && option) {
      const newClasses = [...selectedClasses];
      const classId = option.classId;
      if (!newClasses.find(c => c.id === classId)) {
        newClasses.push({ id: classId, name: option.label });
        setSelectedClasses(newClasses);
      }
      setClassName('');
      setShowClassInput(false);
      setDropdownOpen(false);

      // Notify parent about class change and fetch chapters
      if (onClassChange) {
        onClassChange(classId);
      }
      // Fetch chapters for selected class
      if (fetchAdminChapterFilter && subjectId) {
        fetchAdminChapterFilter('', classId, subjectId);
      }
      // Also trigger onFetchChapters callback
      if (onFetchChapters) {
        onFetchChapters(classId);
      }
    } else {
      setSelectedClasses([]);
      setClassName('');
    }
  };

  const onRemoveClass = (classId) => {
    const newClasses = selectedClasses.filter((item) => item.id !== classId);
    setSelectedClasses(newClasses);
    setClassName('');
  };

  // Resolve typed text back to a real chapter. Matching on the trimmed, case-folded
  // name is what the dropdown itself shows, so "Algebra" and "algebra " both land on
  // the same chapter the user could have picked from the list.
  const findChapterByName = (value) => {
    const needle = (value || '').trim().toLowerCase();
    if (!needle) return null;
    return (chapterList || []).find((c) => {
      if (!c) return false;
      const name = c.name || c.chapterName || '';
      return String(name).trim().toLowerCase() === needle;
    }) || null;
  };

  // Chapter handlers
  const handleSearchChapter = (value) => {
    setChapterSearchText(value || null);
    if (!value) {
      setChapterName('');
      setChapterId(null);
      setTopicId(null);
      setTopicName('');
      setTopicSearchText(null);
      setShowTopicError(false);
      return;
    }
    setChapterName(value);
    // Typing over a selection invalidates it - the text no longer names the chapter
    // chapterId still points at. Previously the id survived, so after picking a
    // chapter and typing junk the Topic field stayed enabled against a stale chapter
    // and the resource could be saved against it. Drop the id (and the topic hanging
    // off it) unless what was typed is itself an exact chapter name.
    const matched = findChapterByName(value);
    const matchedId = matched ? (matched.id || matched.chapterId || matched.chapterid) : null;
    if (matchedId !== chapterId) {
      setChapterId(matchedId || null);
      setTopicId(null);
      setTopicName('');
      setTopicSearchText(null);
      setShowTopicError(false);
    }
  };

  const onSelectChapter = (value, option) => {
    if (value && option) {
      const actualChapterId = option.chapterId;
      setChapterId(actualChapterId);
      setChapterName(option.label);
      // Back to "showing a selection", so the next open lists every chapter again.
      setChapterSearchText(null);
      setTopicSearchText(null);

      // Clear topic (it belonged to the previous chapter) — but leave the title alone;
      // changing chapter/topic shouldn't wipe out a title the user already typed (or
      // that was already there from editing an existing resource). Only the user
      // clearing the Title field themselves should clear it.
      setTopicId(null);
      setTopicName('');
      setShowTopicError(false);
      setShowChapterDropdown(false);
      // Fetch topics for selected chapter
      if (fetchFilterTopic && subjectId) {
        // Get the first class ID from selectedClasses
        const classId = selectedClasses.length > 0 ? selectedClasses[0].id : null;
        if (classId) {
          fetchFilterTopic(subjectId, classId, actualChapterId);
        }
      }
      // Also trigger onFetchTopics callback
      if (onFetchTopics) {
        onFetchTopics(actualChapterId);
      }
    } else {
      // Chapter field cleared (the "x") — only clear the chapter itself. The topic
      // stays as-is, same reasoning as the title above: clearing one field shouldn't
      // silently wipe out another one the user didn't touch.
      setChapterId(null);
      setChapterName('');
      setChapterSearchText(null);
    }
  };

  // Topic handlers
  const handleSearchTopic = (value) => {
    setTopicSearchText(value || null);
    if (value) setTopicName(value);
    else setTopicName('');
  };

  const onSelectTopic = (value, option) => {
    // allow creating a new topic when sentinel value -1 selected
    if (value === -1) {
      if (!chapterId || !option.topicNamePayload) {
        message.error('Please select a chapter and enter a topic name');
        return;
      }
      if (postCreateTopic) {
        // Create topic with chapterId and topicName
        const topicObject = {
          chapterId: chapterId ? parseInt(chapterId) : null,
          topicName: option.topicNamePayload
        };
        // Reset topicName just like uploadfiles.js
        setTopicName('');
        setTopicSearchText(null);
        postCreateTopic(topicObject, () => { }, () => {
          // On success, refresh topics list
          // message.success('Topic created successfully');
          if (fetchFilterTopic && subjectId) {
            const classId = selectedClasses.length > 0 ? selectedClasses[0].id : null;
            if (classId) {
              fetchFilterTopic(subjectId, classId, chapterId);
            }
          }
        });
      }
      return;
    }

    if (value && option) {
      const actualTopicId = option.topicId;
      setTopicId(actualTopicId);
      setTopicName(option.label || value);
      setShowTopicDropdown(false);
      // Showing a selection again - next open lists every topic, not just this one.
      setTopicSearchText(null);
    } else {
      setTopicId(null);
      setTopicName('');
      setTopicSearchText(null);
    }
  };

  const handleSaveAttempt = () => {
    if (topicName && !topicId) {
      setShowTopicError(true);
      // scroll to reveal error if needed (basic feedback)
      return;
    }
    setShowTopicError(false);
    setShowSaveModal(true);
  };

  // File upload handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
      if (e.dataTransfer.items) {
        for (let i = 0; i < e.dataTransfer.items.length; i++) {
          if (e.dataTransfer.items[i].kind === 'file') {
            const type = e.dataTransfer.items[i].type;
            if (type.startsWith('video/')) setDragType('video');
            else if (type === 'application/pdf') setDragType('pdf');
          }
        }
      }
    } else if (e.type === "dragleave") {
      setDragActive(false);
      setDragType(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
        if (initialData) {
          setIsInitialDataRemoved(true);
        }
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
        if (initialData) {
          setIsInitialDataRemoved(true);
        }
      }
      e.target.value = ""; // Reset value to allow repeat selection
    }
  };

  const validateFile = (file) => {
    const fileName = (file?.name || "").toLowerCase();
    const isVideo = isVideoFile(file);
    const isPdf = file.type === 'application/pdf' || fileName.endsWith('.pdf');

    // Limits
    const videoMaxSize = 10 * 1024 * 1024 * 1024; // 10GB
    const pdfMaxSize = 70 * 1024 * 1024; // 70MB
    const allowedTypes = [
      'application/pdf',
      'video/mp4',
      'video/avi',
      'video/x-msvideo',
      'video/x-flv',
      'video/quicktime',
      'video/x-matroska',
      'video/webm'
    ];
    const allowedExtensions = ['.pdf', '.mp4', '.avi', '.flv', '.mov', '.mkv', '.webm'];
    const hasAllowedExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (isVideo && file.size > videoMaxSize) {
      showCustomNotification('error', 'Error', 'Video file size should not exceed 10GB');
      return false;
    }

    if (isPdf && file.size > pdfMaxSize) {
      showCustomNotification('error', 'Error', 'PDF file size should not exceed 70MB');
      return false;
    }

    // Same "not enough storage" check as the Course/Library upload flows — the file is
    // bigger than whatever space is actually left, so block it here and surface the
    // upgrade-storage modal instead of letting the upload start and fail later.
    const spaceAvailable = sizeOfContents?.spaceAvailable;
    if (spaceAvailable != null && !isNaN(spaceAvailable) && file.size > spaceAvailable) {
      setAvailableStorageBytes(spaceAvailable);
      setStorageModalVisible(true);
      return false;
    }

    if (!allowedTypes.includes(file.type) && !hasAllowedExtension) {
      showCustomNotification('error', 'Error', 'Only MP4, AVI, FLV, MOV, MKV files are supported');
      return false;
    }



    return true;
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPdfType(null);
    setShowPdfTypeDropdown(false);
    // Page count and the size/pages caption describe the file being removed. Left
    // standing, the old PDF's page count rode along in the next upload's
    // documentPages (the effect that recomputes them only runs once a new file is
    // actually picked, so there is a window where they describe nothing at all).
    setPdfPageCount(0);
    setVideoDuration(0);
    setVideoDurationSec(0);
    setFileMetadata('');
    // If we're in edit mode, ensure the form doesn't think the initial file is still there
    if (initialData) {
      setIsInitialDataRemoved(true);
    }
    // Reset the file input
    const fileInput = document.getElementById('file-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Same mins+secs formatting as ResourcesPanel.js's ResourceItem - keeps the
  // leftover seconds instead of rounding them away, so a 4 min 27 sec video
  // reads as "4 mins 27 secs" here too, not just after the upload finishes.
  const formatDuration = (totalSeconds) => {
    const seconds = Math.max(0, Math.round(totalSeconds));
    if (seconds < 60) {
      return `${Math.max(1, seconds)} sec`;
    }
    const mins = Math.floor(seconds / 60);
    const remainderSecs = seconds % 60;
    if (remainderSecs === 0) {
      return mins === 1 ? '1 min' : `${mins} mins`;
    }
    const minsLabel = mins === 1 ? '1 min' : `${mins} mins`;
    const secsLabel = remainderSecs === 1 ? '1 sec' : `${remainderSecs} secs`;
    return `${minsLabel} ${secsLabel}`;
  };

  // Restore this to go back to reporting everything under a gigabyte in MB (a
  // 102 KB recording then reads "0.10 MB" here while ResourcesPanel's list says
  // "102.0 KB" for the same file):
  // const formatFileSize = (bytes) => {
  //   if (!bytes || bytes === 0) return '0 MB';
  //   const k = 1024;
  //   const mb = bytes / (k * k);
  //   if (mb >= 1024) {
  //     return `${(mb / k).toFixed(1)} GB`;
  //   }
  //   if (mb < 1 && mb > 0) {
  //     return `${mb.toFixed(2)} MB`;
  //   } else {
  //     return `${mb.toFixed(1)} MB`;
  //   }
  // };
  // Returns null for a size we don't have, so the caller can leave the size out
  // of the line entirely rather than claiming a playable file is 0 bytes - same
  // reasoning as ResourcesPanel.js's getMetadata.
  const formatFileSize = (bytes) => {
    const numeric = Number(bytes);
    if (!numeric || Number.isNaN(numeric) || numeric <= 0) return null;
    // Guard: Video recording sizes under 10 KB (e.g. 500 bytes error text) are invalid
    if (isRecording && numeric < 10240) return null;
    const k = 1024;
    const mb = numeric / (k * k);
    if (mb >= 1024) {
      return `${(mb / k).toFixed(1)} GB`;
    }
    if (mb < 1) {
      // Same KB threshold and rounding ResourcesPanel.js uses, so a short recording
      // doesn't read as "102.0 KB" in the list and "0.10 MB" in this form.
      const kb = numeric / k;
      return `${kb < 1 ? kb.toFixed(2) : kb.toFixed(1)} KB`;
    }
    return `${mb.toFixed(1)} MB`;
  };

  const isVideoFile = (file) => {
    if (!file) return false;
    const fileName = (file?.name || "").toLowerCase();
    const videoTypes = [
      'video/mp4',
      'video/avi',
      'video/x-msvideo',
      'video/x-flv',
      'video/quicktime',
      'video/x-matroska',
      'video/webm'
    ];
    const videoExtensions = ['.mp4', '.avi', '.flv', '.mov', '.mkv', '.webm'];
    return videoTypes.includes(file.type) || videoExtensions.some(ext => fileName.endsWith(ext));
  };

  // YouTube validation helper
  const validateYoutubeUrl = (url) => {
    if (!url) return true;

    // 1. URL contains youtube.com OR youtu.be
    const hasDomain = url.includes('youtube.com') || url.includes('youtu.be');

    // 4. No playlist parameter
    const noPlaylist = !url.includes('list=');

    // 2 & 3. Extracted video ID exists and length is 11
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    const videoId = (match && match[2].length === 11) ? match[2] : null;

    return hasDomain && noPlaylist && videoId !== null;
  };

  const isYoutubeValid = youtubeLink ? validateYoutubeUrl(youtubeLink) : true;
  // The "Invalid URL" notice and the hover "x" both sit absolutely on the right-hand
  // end of the YouTube input, so the input has to give back the room each one needs -
  // otherwise a long enough link just runs underneath and the two render on top of
  // each other. They are mutually exclusive: the notice hides while hovered, which is
  // exactly when the clear button appears.
  const showYoutubeInvalid = Boolean(youtubeLink) && !isYoutubeValid && !hasLocalVideoMedia && !isYoutubeHovered;
  const showYoutubeClear = Boolean(youtubeLink) && isYoutubeHovered && !hasLocalVideoMedia;
  // ~"Invalid URL" at 14px, plus a gap so the text stops short of it rather than
  // butting up against it.
  const youtubeInputPaddingRight = showYoutubeInvalid ? 84 : (showYoutubeClear ? 30 : 0);

  // Extract metadata when file changes
  useEffect(() => {
    if (!selectedFile) {
      setFileMetadata('');
      return;
    }

    if (selectedFile.type === 'application/pdf') {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const pdfDoc = await PDFDocument.load(e.target.result);
          const pages = pdfDoc.getPageCount();
          setPdfPageCount(pages);
          setFileMetadata(` | ${pages} pages`);
        } catch (err) {
          console.error("Error loading PDF pages", err);
          setPdfPageCount(0);
          setFileMetadata('');
        }
      };
      reader.readAsArrayBuffer(selectedFile);
    } else if (isVideoFile(selectedFile)) {
      // This used to read the duration off a <video> element directly, which the browser
      // cannot do for .flv or .avi - it fired onerror, the duration stayed 0, and those
      // cards showed none while uploading OR afterwards, since `videoDuration` here is
      // what the upload payload carries and the library row is later rendered from.
      // getLocalVideoDurationSec keeps the <video> path for formats the browser decodes
      // and reads the duration out of the file header for those two.
      let cancelled = false;
      getLocalVideoDurationSec(selectedFile)
        .then((seconds) => {
          if (cancelled) return;
          if (seconds > 0) {
            // videoDuration itself stays whole minutes (that's what's sent to the
            // backend in the upload payload below) - only the on-screen label
            // gets the seconds so it doesn't silently round "4 mins 27 secs"
            // down to "4 mins" while the file is selected/uploading.
            const roundedSec = Math.round(seconds);
            setVideoDurationSec(roundedSec);
            const mins = Math.floor(seconds / 60) || 1;
            setVideoDuration(mins);
            setFileMetadata(` | ${formatDuration(seconds)}`);
          } else {
            setVideoDurationSec(0);
            setVideoDuration(0);
            setFileMetadata('');
          }
        })
        .catch((err) => {
          console.error("Error loading video duration", err);
          if (cancelled) return;
          setVideoDurationSec(0);
          setVideoDuration(0);
          setFileMetadata('');
        });
      return () => { cancelled = true; };
    } else {
      setFileMetadata('');
    }
  }, [selectedFile]);

  // A YouTube resource has no file to measure, so its length comes from the player API
  // instead (see getYoutubeDurationSec). Resolved here rather than at save time so the
  // value is already known when the payload is built, and the card shows it immediately
  // instead of every viewer having to look it up again.
  useEffect(() => {
    if (!youtubeLink || !isYoutubeValid || selectedFile) return undefined;

    let cancelled = false;
    getYoutubeDurationSec(youtubeLink).then((seconds) => {
      if (cancelled) return;
      // Whole minutes, matching what the file path above sends.
      setVideoDuration(seconds > 0 ? (Math.floor(seconds / 60) || 1) : 0);
      setFileMetadata(seconds > 0 ? ` | ${formatDuration(seconds)}` : '');
    });
    return () => { cancelled = true; };
  }, [youtubeLink, isYoutubeValid, selectedFile]);

  // Form validation
  // Chapter/topic don't apply to a class recording (see isRecording above).
  const isFormValid = Boolean(
    subjectId &&
    selectedClasses.length > 0 &&
    (isRecording || chapterId) &&
    (isRecording || topicId || (topicName && topicName.trim())) &&
    (selectedFile || (youtubeLink && isYoutubeValid) || (initialData && !selectedFile && !isInitialDataRemoved)) &&
    title &&
    // If it's a PDF upload, pdfType must be selected
    (
      (selectedFile && isVideoFile(selectedFile)) ||
      (youtubeLink && isYoutubeValid) ||
      (initialData && !isInitialDataRemoved && (initialData.type === 'video' || initialData.type === 'youtube' || initialData.type === 'recording')) ||
      pdfType
    )
  );

  const isFormDirty = (() => {
    // If not in edit mode (no initialData), dirty if any field has a value
    if (!initialData) {
      return Boolean(
        chapterId ||
        chapterName ||
        topicId ||
        topicName ||
        title ||
        youtubeLink ||
        selectedFile ||
        pdfType
      );
    }

    // In edit mode, dirty if any field differs from initialData
    try {
      const initialChapterIdInput = initialData?.chapters_id || initialData?.chapterId || initialData?.chapter_id || null;
      const sameChapter = String(chapterId || '') === String(initialChapterIdInput || '');

      const initialTopicIdInput = initialData?.topics_id || initialData?.topicId || initialData?.topic_id || null;
      const sameTopicId = String(topicId || '') === String(initialTopicIdInput || '');

      const initialTopicNameValue = (() => {
        const val = initialData?.topics_name || initialData?.topic_name || initialData?.topicName || initialData?.topic || '';
        return typeof val === 'object' ? (val?.name || val?.title || val?.topicName || '') : String(val);
      })();
      const sameTopicName = String(topicName || '') === String(initialTopicNameValue || '');

      const sameTitle = title === (initialData?.title || '');

      const initialLinkStr = initialData?.link || (typeof initialData?.file === 'string' ? initialData.file : '');
      const isInitYoutube = initialData?.type === 'youtube' || initialLinkStr.includes('youtube.com') || initialLinkStr.includes('youtu.be');
      const initialYoutubeUrl = isInitYoutube ? initialLinkStr : '';
      const sameYoutube = youtubeLink === (initialYoutubeUrl || '');

      const sameFile = !selectedFile;
      const sameInitialDataRemoved = !isInitialDataRemoved;

      const initialPdfTypeValue = initialData?.type === 'lecture-notes' || initialData?.type === 'question-bank' || initialData?.type === 'pdf' ? (initialData.type === 'pdf' ? 'lecture-notes' : initialData.type) : null;
      const samePdfType = pdfType === initialPdfTypeValue;

      return !(sameChapter && sameTopicId && sameTopicName && sameTitle && sameYoutube && sameFile && sameInitialDataRemoved && samePdfType);
    } catch (e) {
      return false;
    }
  })();

  // Reset handler
  const handleReset = () => {
    // Subject and batch fields are intentionally left unreset
    setChapterId(initialData?.chapters_id || initialData?.chapterId || initialData?.chapter_id || null);
    setChapterName(() => {
      const val = initialData?.chapters_name || initialData?.chapter_name || initialData?.chapterName || initialData?.chapter || '';
      return typeof val === 'object' ? (val?.name || val?.title || val?.chapterName || '') : String(val);
    });
    setTopicId(initialData?.topics_id || initialData?.topicId || initialData?.topic_id || null);
    setTopicName(() => {
      const val = initialData?.topics_name || initialData?.topic_name || initialData?.topicName || initialData?.topic || '';
      return typeof val === 'object' ? (val?.name || val?.title || val?.topicName || '') : String(val);
    });
    setTitle(initialData?.title || '');
    const initialLinkStr = initialData?.link || (typeof initialData?.file === 'string' ? initialData.file : '');
    const isYoutube = initialData?.type === 'youtube' || initialLinkStr.includes('youtube.com') || initialLinkStr.includes('youtu.be');
    setYoutubeLink(isYoutube ? initialLinkStr : '');
    setSelectedFile(null);
    setDropdownOpen(false);
    setPdfType(initialData?.type === 'lecture-notes' || initialData?.type === 'question-bank' || initialData?.type === 'pdf' ? (initialData.type === 'pdf' ? 'lecture-notes' : initialData.type) : null);
    setShowPdfTypeDropdown(false);
    setFileMetadata('');
    setPdfPageCount(0);
    setVideoDuration(0);
    setVideoDurationSec(0);
    setIsInitialDataRemoved(false);
    setShowTopicError(false);

    // Reset the file input DOM element so the same file can be uploaded again
    const fileInput = document.getElementById('file-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Note: parent header close icon will handle navigation back to ResourcesPanel via onReset

  // Chunk upload state (same as uploadfiles.js)
  let fileId = null;
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadProgressCheck, setUploadProgressCheck] = useState(false);
  const [footerWidth, setFooterWidth] = useState(window.innerWidth <= 480 ? '100%' : (window.innerWidth <= 1024 ? '80%' : 592));

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 480) {
        setFooterWidth('100%');
      } else if (window.innerWidth <= 1024) {
        setFooterWidth('80%');
      } else {
        setFooterWidth(592);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Chunk upload function (same logic as uploadfiles.js)
  const uploadFunction = async (file, callback, successCallback) => {
    fileId = uuidv4() + "." + file.name.split('.').pop();
    // Rejects if any block fails, so handleSave's catch resets the form state.
    await uploadFileInChunks({
      file,
      uploadChunk: uploadBlock,
      onProgress: (percent) => setUploadProgress(percent),
    });
    // Only merge once every block is up.
    MergeChunk(fileId, callback, successCallback);
  };

  const uploadBlock = async (currentBlock, orderId) => {
    var params = {
      file: currentBlock,
      orderId: orderId,
      fileName: fileId
    };
    const response = await uploadBlock2(params, () => { });
    if (response?.data?.progress?.message !== "uploaded") {
      throw new Error(`Block ${orderId} was not accepted by the server`);
    }
  };

  // Save handler — uses the same upload integration as uploadfiles.js
  const handleSave = async () => {
    if (!isFormValid) return;

    if (togglePageLoader) togglePageLoader(true);
    const classIds = selectedClasses.map(c => c.id);
    const classId = classIds[0]; // Use first class for chapter/topic queries

    try {
      if (selectedFile && isVideoFile(selectedFile)) {
        if (isRecording && initialData?.roomName) {
          if (!uploadRecordingChunk || !mergeRecordingChunks || !updateRecordingCount || !deleteBroadcastRecording || !BroadcastRecordingEdit) {
            showCustomNotification('error', 'Error', 'Recording replace not configured');
            if (togglePageLoader) togglePageLoader(false);
            return;
          }

          setUploadProgress(0);
          setUploadProgressCheck(true);

          try {
            // 1. Delete existing
            await new Promise((resolve) => deleteBroadcastRecording(initialData.roomName, resolve, resolve, true));

            // 2. Upload chunks
            const recordingFileId = uuidv4() + "." + selectedFile.name.split('.').pop();
            await uploadFileInChunks({
              file: selectedFile,
              uploadChunk: async (chunk, orderId, totalChunks) => {
                await uploadRecordingChunk({ file: chunk, orderId, totalChunks, fileName: recordingFileId, roomName: initialData.roomName });
              },
              onProgress: (percent) => setUploadProgress(percent)
            });

            // 3. Merge chunks
            await mergeRecordingChunks(recordingFileId);

            // Compute formatted duration (HH:mm:ss) and minutes from the uploaded video
            const sec = videoDurationSec > 0 ? videoDurationSec : (videoDuration * 60);
            const hours = Math.floor(sec / 3600);
            const minutes = Math.floor((sec % 3600) / 60);
            const seconds = Math.floor(sec % 60);
            const formattedHms = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
            const durMins = sec > 0 ? (sec / 60) : (videoDuration || 0);

            // 4. Register recording
            await updateRecordingCount({ 
              roomId: initialData.roomName,
              fileSize: selectedFile.size,
              newFileName: selectedFile.name,
              fileDuration: formattedHms,
              fileDurationSec: sec,
              videoDuration: durMins,
              duration: sec
            });

            saveReplacedRecording({
              roomName: initialData.roomName,
              scheduleId: initialData?.occurrenceId || initialData?.id || initialData?.scheduleId,
              fileSize: selectedFile.size,
              fileDuration: formattedHms,
              fileName: selectedFile.name,
              durationSec: sec,
              videoDuration: durMins
            });

            // 5. Rename if changed
            const finalTitle = title.trim();
            if (finalTitle && finalTitle !== initialData.title) {
              await new Promise((resolve, reject) => {
                BroadcastRecordingEdit({ fileName: finalTitle, roomName: initialData.roomName }, resolve, resolve);
              });
            }

            if (togglePageLoader) togglePageLoader(false);
            if (onSave) {
              onSave({ 
                title: finalTitle || title, 
                type: 'recording', 
                isEdit: true, 
                file: selectedFile,
                size: selectedFile.size,
                fileSize: selectedFile.size,
                videoDuration: durMins,
                duration: sec,
                durationSec: sec,
                fileDuration: formattedHms
              });
            }
            handleReset();
            setUploadProgressCheck(false);
          } catch (err) {
            console.error("Recording replace failed:", err);
            if (togglePageLoader) togglePageLoader(false);
            setUploadProgressCheck(false);
            // Rejects on failure so it stops immediately
            // Error toast is already shown by Api layer
          }
          return;
        }

        // Video file - use chunk upload like uploadfiles.js
        if (!uploadBlock2 || !MergeChunk || !uploadCompleted2) {
          showCustomNotification('error', 'Error', 'Upload not configured');
          if (togglePageLoader) togglePageLoader(false);
          return;
        }

        // Note: message.loading replaced by custom logic or handled by loader
        setUploadProgress(0);
        setUploadProgressCheck(true);

        const callbackChunk = () => {
          setUploadProgress(0);
          setUploadProgressCheck(false);
          // Note: we don't hide global loader here yet, we wait for full completion
        };

        const successCallbackMergeChunk = (mergeResponse) => {
          // Call uploadCompleted2 after merge - this is the call that actually
          // creates the library content record (subject/chapter/topic/title
          // association) for a chunk-uploaded video; without it the file sits
          // merged on storage but is never registered as a resource.
          let id = initialData?.id || initialData?.libraryId;
          if (typeof id === 'string' && id.startsWith('class-recording-')) id = null;

          // NOTE: this used to also call postBroadcastVideoUpload (/schedule-new/update-record-count)
          // here, mirroring the digitalLibrary offline-video-upload flow (uploadLocalVideosModal.js /
          // replaceModal.js). Those flows exist specifically to upload a video AS a Broadcast
          // class's substitute recording when it has none - update-record-count is what marks
          // the room as now having a recording. This path is the generic "add a resource"
          // upload, completely unrelated to whether the class itself was recorded; calling it
          // here made every video resource uploaded to a Broadcast-mode class's schedule falsely
          // increment schedule.recordCount, which in turn made ScheduleDetailsDrawer.js
          // synthesize a phantom "Class recording" card for classes that were never recorded.
          // Actually replacing/renaming the real class recording already has its own dedicated
          // path further below (isRecording && initialData.roomName -> BroadcastRecordingEdit).

          var uploadObject = {
            // Do not pass ID when replacing a video - backend transcoder only triggers
            // on new row creation, so patching an existing record bypasses transcoding.
            // We create a new one instead and delete the old one below.
            subjectId: subjectId,
            chapterId: chapterId ? parseInt(chapterId) : null,
            title: title,
            standardId: classId ? parseInt(classId) : null,
            standardIds: JSON.stringify(classIds),
            type: 'video',
            fileName: mergeResponse?.fileKey || fileId,
            chapterName: chapterName || '',
            topicId: topicId,
            topicName: topicName || '',
            videoFileSize: selectedFile?.size,
            occurrenceId: broadcastId != null ? String(broadcastId) : broadcastId,
            occuranceId: broadcastId != null ? String(broadcastId) : broadcastId, // Backward compatibility
            videoDuration: videoDuration,
            documentPages: 0
          };

          uploadCompleted2(uploadObject, async (response) => {
            if (id && deleteResourceContent) {
              try {
                await new Promise(res => deleteResourceContent(id, res, true));
              } catch (e) {
                console.error("Failed to delete old video record", e);
              }
            }
            if (togglePageLoader) togglePageLoader(false);
            if (response != undefined) {

              if (removemodel) removemodel();
              if (onSave) {
                onSave({
                  subjectId,
                  classIds,
                  chapterId,
                  topicId,
                  title,
                  type: 'video',
                  file: selectedFile,
                  videoDuration: videoDuration,
                  documentPages: 0,
                  // Lets the parent (ScheduleDetailsDrawer.js) mark this specific upload as
                  // still-transcoding until the refetched admin-library row confirms otherwise -
                  // it's the same value we sent as `fileName` above, which is what the row
                  // should come back tagged with.
                  fileKey: mergeResponse?.fileKey || fileId,
                  isEdit: !!initialData
                });
              }
              handleReset();
              setUploadProgressCheck(false);
            }
          });
        };

        await uploadFunction(selectedFile, callbackChunk, successCallbackMergeChunk);

      } else if (selectedFile) {
        // PDF / lecture notes - use uploadlecturenotes like uploadfiles.js
        if (!uploadlecturenotes) {
          showCustomNotification('error', 'Error', 'Upload not configured');
          if (togglePageLoader) togglePageLoader(false);
          return;
        }

        setUploadProgress(0);
        setUploadProgressCheck(true);

        let id = initialData?.id || initialData?.libraryId;
        if (typeof id === 'string' && id.startsWith('class-recording-')) id = null;
        // Do not pass ID when replacing a PDF — same reason the video path above
        // doesn't: POST /admin-library/content with an `id` is read by the backend as
        // "update this row's metadata" and leaves the stored file untouched, so the
        // replacement uploaded but View kept serving the old document even after a
        // hard refresh. Digital Library never combines the two either — it creates
        // with a file and no id (uploadfiles.js), and edits metadata by PATCH with an
        // id and no file (editInformation.js). Create a new row and delete the old one
        // below, in that order: a failed upload then costs nothing, where deleting
        // first would lose the resource outright.
        const uploadObject = {
          subjectId: subjectId,
          chapterId: chapterId ? parseInt(chapterId) : null,
          title: title,
          standardId: classId ? parseInt(classId) : null,
          standardIds: JSON.stringify(classIds),
          type: pdfType || 'lecture-notes',
          file: new File([selectedFile], uuidv4() + '_' + selectedFile.name, { type: selectedFile.type }),
          chapterName: chapterName || '',
          topicId: topicId,
          topicName: topicName || '',
          occurrenceId: broadcastId != null ? String(broadcastId) : broadcastId,
          occuranceId: broadcastId != null ? String(broadcastId) : broadcastId, // Backward compatibility
          documentPages: pdfPageCount,
          videoDuration: 0,
          size: selectedFile.size,
        };
        console.log("UPLOAD PAYLOAD (PDF):", uploadObject);

        uploadlecturenotes(
          uploadObject,
          () => {
            if (togglePageLoader) togglePageLoader(false);
            setUploadProgressCheck(false);
          },
          async () => {
            // The replacement row exists now, so the old one can go. Awaited before
            // onSave because onSave is what refetches the list - delete after it and
            // the list comes back holding both copies until something refetches again.
            // Mirrors the video replace path above, including swallowing a failed
            // delete: the new PDF is already saved, and leaving a stale duplicate
            // visible beats failing a save that actually succeeded.
            if (id && deleteResourceContent) {
              try {
                await new Promise(res => deleteResourceContent(id, res, true));
              } catch (e) {
                console.error("Failed to delete old PDF record", e);
              }
            }
            if (togglePageLoader) togglePageLoader(false);
            // Reset the shared Library "uploadsuccessful" flag - see comment above.
            if (removemodel) removemodel();
            if (onSave) {
              onSave({
                subjectId,
                classIds,
                chapterId,
                topicId,
                title,
                type: pdfType || 'lecture-notes',
                file: selectedFile,
                documentPages: pdfPageCount,
                videoDuration: 0,
                size: selectedFile.size,
                isEdit: !!initialData
              });
            }
            handleReset();
          },
          setUploadProgress
        );
      } else if (youtubeLink) {
        if (!isYoutubeValid) {
          showCustomNotification('error', 'Error', 'Please enter a valid YouTube link');
          if (togglePageLoader) togglePageLoader(false);
          return;
        }
        // YouTube link - use uploadCompleted2 with link
        if (!uploadCompleted2) {
          if (onSave) {
            onSave({ subjectId, classIds, chapterId, topicId, title, youtubeLink, type: 'youtube', isEdit: !!initialData });
            handleReset();
            if (togglePageLoader) togglePageLoader(false);
            return;
          }
          showCustomNotification('error', 'Error', 'Upload not configured');
          if (togglePageLoader) togglePageLoader(false);
          return;
        }

        let id = initialData?.id || initialData?.libraryId;
        if (typeof id === 'string' && id.startsWith('class-recording-')) id = null;
        // Did the user actually swap the link, or just retitle/recategorise an
        // existing YouTube resource? Both land in this branch, because youtubeLink is
        // pre-filled from initialData on open.
        const youtubeLinkReplaced = Boolean(id) && youtubeLink !== initialYoutubeUrl;
        // Same backend constraint the PDF and video paths hit: POST
        // /admin-library/content with an `id` updates the row's metadata and leaves
        // the stored media alone, so a swapped link uploaded but Play kept serving the
        // old video. Replacing means create-new-then-delete-old; a metadata-only edit
        // still goes through as a plain update, which is what `id` is actually for.
        const uploadObject = {
          ...(id && !youtubeLinkReplaced && { id }),
          subjectId: subjectId,
          chapterId: chapterId ? parseInt(chapterId) : null,
          title: title,
          standardId: classId ? parseInt(classId) : null,
          standardIds: JSON.stringify(classIds),
          type: 'video',
          link: youtubeLink,
          chapterName: chapterName || '',
          topicId: topicId,
          topicName: topicName || '',
          occurrenceId: broadcastId != null ? String(broadcastId) : broadcastId,
          occuranceId: broadcastId != null ? String(broadcastId) : broadcastId, // Backward compatibility
          documentPages: 0,
          videoDuration: videoDuration,
          videoThumbnail: null
        };

        uploadCompleted2(uploadObject, async (response) => {
          if (response != undefined) {
            // Old row goes only once the replacement exists, and before onSave (which
            // refetches the list) so the list never comes back holding both links.
            // A failed delete is logged, not surfaced: the new link is already saved.
            if (youtubeLinkReplaced && deleteResourceContent) {
              try {
                await new Promise(res => deleteResourceContent(id, res, true));
              } catch (e) {
                console.error("Failed to delete old YouTube record", e);
              }
            }
            if (togglePageLoader) togglePageLoader(false);
            // Reset the shared Library "uploadsuccessful" flag - see comment above.
            if (removemodel) removemodel();
            if (onSave) {
              onSave({ subjectId, classIds, chapterId, topicId, title, youtubeLink, type: 'youtube', isEdit: !!initialData });
            }
            handleReset();
          } else {
            if (togglePageLoader) togglePageLoader(false);
          }
        });
      } else if (initialData && isRecording && initialData.roomName) {
        if (!BroadcastRecordingEdit) {
          showCustomNotification('error', 'Error', 'Update not configured');
          if (togglePageLoader) togglePageLoader(false);
          return;
        }
        let recordingRenameSucceeded = false;
        BroadcastRecordingEdit(
          { fileName: title, roomName: initialData.roomName },
          () => {

            if (togglePageLoader) togglePageLoader(false);
            if (!recordingRenameSucceeded) {
              showCustomNotification('error', 'Error', 'Failed to update resource');
            }
          },
          () => {
            if (recordingRenameSucceeded) return; // fires twice on success - only act once
            recordingRenameSucceeded = true;
            if (onSave) {
              onSave({ title, type: 'recording', isEdit: true });
            }
            handleReset();
          }
        );
      } else if (initialData) {
        // Edit mode without new file/link change - update metadata
        if (!patchResourceTitle) {
          showCustomNotification('error', 'Error', 'Update not configured');
          if (togglePageLoader) togglePageLoader(false);
          return;
        }

        let id = initialData.id || initialData.libraryId;
        if (typeof id === 'string' && id.startsWith('class-recording-')) {
          showCustomNotification('error', 'Error', 'Cannot update a virtual recording without replacing its file');
          if (togglePageLoader) togglePageLoader(false);
          return;
        }

        const updatePayload = {
          title,
          subjectId,
          standardIds: JSON.stringify(selectedClasses.map(c => c.id || c.standardId || c.classId)),
          // A class recording has no chapter/topic (see isRecording above) -
          // don't send these at all rather than null/undefined for it.
          ...(isRecording ? {} : {
            chapterId: chapterId ? parseInt(chapterId) : null,
            topicId,
          }),
        };

        patchResourceTitle(id, updatePayload, (response, error) => {
          if (togglePageLoader) togglePageLoader(false);
          if (response && !error) {
            if (onSave) {
              onSave({ ...updatePayload, classIds: selectedClasses.map(c => c.id || c.standardId || c.classId), isEdit: true });
            }
            handleReset();
          } else {
            showCustomNotification('error', 'Error', 'Failed to update resource');
          }
        });
      }
    } catch (e) {
      console.error("Error in handleSave:", e);
      if (togglePageLoader) togglePageLoader(false);
      showCustomNotification('error', 'Error', 'Failed to save resource');
      setUploadProgressCheck(false);
    }
  };

  return (
    <div className="resource-add-wrapper">
      <div className="resource-add-content">


        {/* Upload Progress */}
        {uploadProgressCheck && (
          <>
            {ReactDOM.createPortal(<PageDataLoader visible={true} />, document.body)}
            <div className="resource-field">
              <div style={{ marginBottom: '8px', fontSize: '14px', color: '#191919', fontWeight: 500 }}>
                Uploading... {uploadProgress}%
              </div>
              <Progress
                percent={uploadProgress}
                status="active"
                strokeColor="#594099"
                style={{ width: '100%' }}
                showInfo={false}
              />
            </div>
          </>
        )}

        {/* Everything below is inert while an upload is running. The payload is already
            on its way to the server, so a click that changed the subject, batch, chapter,
            topic or title now would be silently ignored - and swapping or removing the
            file mid-upload would leave the form describing something other than what is
            being sent. The progress bar above sits outside this wrapper so it stays at
            full contrast. */}
        <div className={uploadProgressCheck ? 'resource-fields-locked' : undefined}>

        {/* File Upload Area */}
        {selectedFile ? (
          // Show uploaded file with REMOVE button (Horizontal Layout)
          <div
            className="resource-field"
            style={{
              width: '100%',
              border: 'none',
              borderRadius: 12,
              background: '#E7F3FF',
              backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%231089FF' stroke-width='2' stroke-dasharray='8, 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '82px 24px',
              marginBottom: 24,
              overflow: 'hidden'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 0 }}>
              <img
                src={isVideoFile(selectedFile) ? videoIcon : pdfIcon}
                alt={isVideoFile(selectedFile) ? "video icon" : "pdf icon"}
                style={{ width: 40, height: 40 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 16,
                  fontWeight: 400,
                  color: '#191919',
                  marginBottom: 4,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%'
                }}>
                  {selectedFile.name}
                </div>
                <div style={{
                  fontSize: 12,
                  fontWeight: 400,
                  color: '#636363',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {/* A file the user just picked has an authoritative size straight
                      from the browser, so 0 here really does mean an empty file -
                      unlike initialData.size below, where 0 means "not known". */}
                  {formatFileSize(selectedFile.size) || '0 KB'}
                  {fileMetadata}
                </div>
              </div>
            </div>
            <div
              onClick={handleRemoveFile}
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: '#FF414D',
                cursor: 'pointer',
                letterSpacing: '0%',
                lineHeight: '100%',
                flexShrink: 0,
                marginLeft: 12
              }}
            >
              remove
            </div>
          </div>
        ) : (initialData && !youtubeLink && !isInitialDataRemoved) ? (
          // Show current resource in Edit Mode (Horizontal Layout like selected file)
          <div
            className="resource-field"
            style={{
              width: '100%',
              border: 'none',
              borderRadius: 12,
              background: '#E7F3FF',
              backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%231089FF' stroke-width='2' stroke-dasharray='8, 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '82px 24px',
              marginBottom: 24,
              overflow: 'hidden'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 0 }}>
              <img
                src={initialData.type === 'pdf' || initialData.type === 'lecture-notes' ? pdfIcon : videoIcon}
                alt="type"
                style={{ width: 40, height: 40 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 16,
                  fontWeight: 400,
                  color: '#191919',
                  marginBottom: 4,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%'
                }}>
                  {(initialData.file || initialData.fileName || initialData.title)?.split('/').pop()?.split('\\').pop()}
                </div>
                <div style={{
                  fontSize: 12,
                  fontWeight: 400,
                  color: '#636363',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {/* Joined rather than concatenated with a hardcoded " | " prefix so
                      an unknown size drops out cleanly instead of leaving the line
                      starting with a separator. Restore this to print a literal
                      fallback for an unknown size:
                      {initialData.size ? formatFileSize(initialData.size) : '0.00 MB'}
                      {initialData.documentPages ? ` | ${initialData.documentPages} pages` : (effectiveInitialDurationSeconds ? ` | ${formatDuration(effectiveInitialDurationSeconds)}` : '')} */}
                  {[
                    formatFileSize(initialData.size),
                    initialData.documentPages
                      ? `${initialData.documentPages} pages`
                      : (effectiveInitialDurationSeconds ? formatDuration(effectiveInitialDurationSeconds) : null),
                  ].filter(Boolean).join(' | ')}
                </div>
              </div>
            </div>
            <div
              // handleRemoveFile, not a bare setIsInitialDataRemoved(true): this is the
              // edit-mode twin of the "remove" on a freshly picked file, and it has to
              // tear down the same state. Flipping only the flag left pdfType holding
              // the *old* PDF's type, so replacing a Notes file with a new PDF silently
              // pre-filled "Notes" for it - the type has to be chosen for the new file.
              onClick={handleRemoveFile}
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: '#FF414D',
                cursor: 'pointer',
                letterSpacing: '0%',
                lineHeight: '100%',
                flexShrink: 0,
                marginLeft: 12
              }}
            >
              remove
            </div>
          </div>
        ) : (
          // Show empty upload area
          <div
            className="resource-field"
            style={{
              width: '100%',
              border: 'none',
              borderRadius: 12,
              background: dragActive ? '#F0EDFF' : '#E7F3FF',
              backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='${encodeURIComponent(hasYoutubeMedia ? '#E0E0E0' : (dragActive ? '#594099' : '#1089FF'))}' stroke-width='2' stroke-dasharray='8, 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              padding: '32px 24px',
              cursor: hasYoutubeMedia ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              position: 'relative',
              marginBottom: 24
            }}
            onClick={hasYoutubeMedia ? null : () => document.getElementById('file-upload').click()}
            onDragEnter={hasYoutubeMedia ? null : handleDrag}
            onDragLeave={hasYoutubeMedia ? null : handleDrag}
            onDragOver={hasYoutubeMedia ? null : handleDrag}
            onDrop={hasYoutubeMedia ? null : handleDrop}
          >
            {hasYoutubeMedia && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: '#E7F3FF',
                opacity: 0.7,
                borderRadius: 12,
                pointerEvents: 'none'
              }} />
            )}
            <div style={{
              fontSize: 16,
              fontWeight: 700,
              color: hasYoutubeMedia ? '#D0D0D0' : '#191919',
              lineHeight: '100%',
              position: 'relative',
              zIndex: 1
            }}>
              Drag and drop files here <span style={{ color: '#D32F2F', marginLeft: 4 }}>*</span>
            </div>
            <div style={{
              fontSize: 12,
              fontWeight: 400,
              color: hasYoutubeMedia ? '#D0D0D0' : '#636363',
              lineHeight: '100%',
              position: 'relative',
              zIndex: 1
            }}>
              {dragActive && dragType === 'video' ? 'Maximum size: 10 GB' :
                (dragActive && dragType === 'pdf' ? 'Max PDF size: 70 MB' :
                  (isRecording ? 'Files supported: MP4, AVI, FLV, MOV, MKV' : 'Files supported: PDF, MP4, AVI, FLV, MOV, MKV'))}
            </div>
            <button
              onClick={(e) => {
                if (!hasYoutubeMedia) {
                  e.stopPropagation();
                  document.getElementById('file-upload').click();
                }
              }}
              disabled={!!hasYoutubeMedia}
              style={{
                width: 122,
                height: 40,
                boxSizing: 'border-box',
                borderRadius: 8,
                border: 'none',
                background: hasYoutubeMedia ? '#D0D0D0' : '#1089FF',
                color: '#FFFFFF',
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
                cursor: hasYoutubeMedia ? 'not-allowed' : 'pointer',
                letterSpacing: '0.5px',
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              CHOOSE FILE
            </button>
            <div style={{
              fontSize: 12,
              fontWeight: 400,
              color: hasYoutubeMedia ? '#D0D0D0' : '#636363',
              lineHeight: '100%',
              position: 'relative',
              zIndex: 1
            }}>
              {/* Both limits, since this box accepts PDFs as well as video and
                  validateFile enforces a different ceiling for each. */}
              {isRecording ? 'Maximum size: 10 GB' : 'Maximum size: 10 GB for video, 70 MB for PDF'}
            </div>
          </div>
        )}


        <input
          id="file-upload"
          type="file"
          accept={isRecording ? ".mp4,.avi,.flv,.mov,.mkv" : ".pdf,.mp4,.avi,.flv,.mov,.mkv"}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {/* OR Divider and YouTube Section - HIDDEN for PDF uploads.
            No longer hidden for completed classes: attaching a YouTube link to a
            class that has already happened is exactly as valid as uploading a file
            to it, and the upload box above was always offered there. Nothing else
            in this form branched on isCompletedClass - validation, duration lookup
            (getYoutubeDurationSec) and the save path below are all status-agnostic -
            so the field was only ever hidden, never unsupported. */}
        {!((selectedFile && !isVideoFile(selectedFile)) ||
          (initialData && (initialData.type === 'pdf' || initialData.type === 'lecture-notes') && !selectedFile && !isInitialDataRemoved)) && (
            <>
              {/* OR Divider */}
              <div className="resource-field" style={{
                fontSize: 12,
                fontWeight: 400,
                color: hasLocalVideoMedia ? '#AEAEAE' : '#636363',
                textAlign: 'center',
                lineHeight: '100%',
                marginBottom: 24
              }}>
                OR
              </div>

              {/* YouTube Link Section */}
              <div className="resource-field" style={{ opacity: 1 }}>
                <div style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: hasLocalVideoMedia ? '#AEAEAE' : '#191919',
                  textAlign: 'center',
                  marginBottom: 16,
                  lineHeight: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  Attach a YouTube link <span style={{ color: hasLocalVideoMedia ? '#AEAEAE' : '#D32F2F', marginLeft: 4 }}>*</span>
                </div>

                {/* Floating Label (appears on focus or value) */}
                {(youtubeLink || isYoutubeFocused) && (
                  <div style={{
                    fontSize: 12,
                    color: hasLocalVideoMedia ? '#AEAEAE' : '#636363',
                    marginBottom: 0,
                    marginTop: -4,
                    fontWeight: 400
                  }}>
                    YouTube link <span style={{ color: hasLocalVideoMedia ? '#AEAEAE' : '#D32F2F', marginLeft: 4 }}>*</span>
                  </div>
                )}

                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', opacity: hasLocalVideoMedia ? 0.6 : 1 }}>
                  {(!youtubeLink && !isYoutubeFocused) && (
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      pointerEvents: 'none',
                      fontSize: '16px',
                      color: hasLocalVideoMedia ? '#E6E6E6' : '#636363',
                      fontFamily: 'Roboto',
                      lineHeight: '100%',
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      Enter a valid YouTube link
                    </div>
                  )}
                  <Input
                    value={youtubeLink}
                    disabled={hasLocalVideoMedia}
                    onChange={(e) => {
                      setYoutubeLink(e.target.value);
                      if (initialData) {
                        setIsInitialDataRemoved(true);
                      }
                    }}
                    onFocus={() => setIsYoutubeFocused(true)}
                    onBlur={() => setIsYoutubeFocused(false)}
                    placeholder=""
                    bordered={false}
                    onMouseEnter={() => setIsYoutubeHovered(true)}
                    onMouseLeave={() => setIsYoutubeHovered(false)}
                    style={{
                      fontSize: 16,
                      color: hasLocalVideoMedia ? '#E6E6E6' : '#191919',
                      fontWeight: 400,
                      flex: 1,
                      zIndex: 2,
                      background: 'transparent',
                      height: 'auto',
                      paddingRight: youtubeInputPaddingRight
                    }}
                  />
                  {showYoutubeClear && (
                    <div
                      onClick={() => {
                        setYoutubeLink('');
                        if (initialData) setIsInitialDataRemoved(true);
                      }}
                      style={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: '#F0F0F0',
                        color: '#636363',
                        fontSize: 12,
                        fontWeight: 'bold',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={() => setIsYoutubeHovered(true)}
                      onMouseLeave={() => setIsYoutubeHovered(false)}
                    >
                      ✕
                    </div>
                  )}
                  {showYoutubeInvalid && (
                    <div style={{
                      color: '#FF414D',
                      fontSize: 14,
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                      position: 'absolute',
                      right: 0,
                      zIndex: 3,
                      // The input scrolls its own text, so a long link can still reach
                      // this edge mid-scroll - paint a ground under the notice so it
                      // stays readable instead of sitting on top of the characters.
                      background: '#FFFFFF',
                      paddingLeft: 8
                    }}>
                      Invalid URL
                    </div>
                  )}
                </div>
                <div style={{ width: '100%', height: '0px', borderTop: `1px solid ${hasLocalVideoMedia ? '#E6E6E6' : '#E6E6E6'}`, opacity: 1, marginTop: 4 }} />
              </div>
            </>
          )}

        {
          ((selectedFile && !isVideoFile(selectedFile)) || (initialData && (initialData.type === 'pdf' || initialData.type === 'lecture-notes') && !selectedFile && !youtubeLink && !isInitialDataRemoved)) && (
            /* PDF Type - only show when PDF is uploaded */
            <div className="resource-field">
              {pdfType && (
                <div style={{
                  fontSize: 12,
                  color: '#636363',
                  marginBottom: 0,
                  fontWeight: 400
                }}>
                  {/* The required marker belongs on the floating label too, not only on
                      the "Select pdf type *" placeholder - PDF type is enforced by
                      isFormValid, so it stays mandatory after a type is picked. Chapter,
                      Topic and Title all mark their floating labels the same way. */}
                  PDF type <span style={{ color: '#D32F2F', marginLeft: 4 }}>*</span>
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <div
                  onClick={() => setShowPdfTypeDropdown(!showPdfTypeDropdown)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: !pdfType ? '12px 0' : '8px 0',
                  }}
                >
                  <span style={{
                    fontSize: 16,
                    color: !pdfType ? '#636363' : '#191919',
                    fontWeight: 400
                  }}>
                    {pdfType === 'lecture-notes' ? 'Notes' :
                      pdfType === 'question-bank' ? 'Question bank' : (
                        <span>Select pdf type <span style={{ color: '#D32F2F', marginLeft: 4 }}>*</span></span>
                      )}
                  </span>
                  <img
                    src={showPdfTypeDropdown ? upArrowIcon : downArrowIcon}
                    alt="arrow"
                    style={{ width: 20, height: 20, opacity: 0.6 }}
                  />
                </div>

                {showPdfTypeDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: '#FFFFFF',
                    boxShadow: '0px 4px 18px rgba(48, 42, 95, 0.08)',
                    // Shadow alone reads as "total white" against this form's white
                    // background - same fix as the batch dropdowns in
                    // ScheduleEditTab.js/CreateScheduleDrawer.js: add a hairline border.
                    border: '1px solid #E6E6E6',
                    borderRadius: 12,
                    zIndex: 100,
                    marginTop: 4,
                    padding: '8px 4px',
                    boxSizing: 'border-box'
                  }}>
                    <div
                      className="pdf-type-item"
                      onClick={() => {
                        setPdfType('lecture-notes');
                        setShowPdfTypeDropdown(false);
                      }}
                      style={{
                        margin: 4,
                        padding: '6px 16px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        background: pdfType === 'lecture-notes' ? '#C6C2FF' : 'transparent',
                        color: pdfType === 'lecture-notes' ? '#191919' : '#636363',
                        fontSize: 14,
                        fontWeight: 400
                      }}
                    >
                      Notes
                    </div>
                    <div
                      className="pdf-type-item"
                      onClick={() => {
                        setPdfType('question-bank');
                        setShowPdfTypeDropdown(false);
                      }}
                      style={{
                        margin: 4,
                        padding: '6px 16px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        background: pdfType === 'question-bank' ? '#C6C2FF' : 'transparent',
                        color: pdfType === 'question-bank' ? '#191919' : '#636363',
                        fontSize: 14,
                        fontWeight: 400
                      }}
                    >
                      Question bank
                    </div>
                  </div>
                )}
                <div style={{ width: '100%', height: '0px', borderTop: '1px solid #E6E6E6', opacity: 1 }} />
              </div>
            </div>
          )
        }

        {/* Subject Field */}
        <div className="resource-field" style={{
          width: '100%',
          height: 59,
          background: '#F4F0FF',
          borderRadius: 12,
          padding: '8px 16px',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          justifyContent: 'center',
          marginBottom: 16,
          cursor: 'not-allowed' // Show disabled cursor
        }}>
          <div style={{
            fontSize: '12px',
            color: '#636363',
            fontFamily: 'Roboto',
            fontWeight: 400,
            lineHeight: '100%',
            marginBottom: 6
          }}>
            Subject <span style={{ color: '#D32F2F', marginLeft: 4 }}>*</span>
          </div>
          <div style={{
            fontSize: '14px',
            color: '#191919',
            fontFamily: 'Roboto',
            fontWeight: 400,
            lineHeight: 'normal',
            width: 175, // Matching spec
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {subjectName || 'Select subject'}
          </div>
        </div>

        {/* Batch Field */}
        <div className="resource-field" style={{
          width: '100%',
          minHeight: 59, // Match the Subject field's box so the pink background reads the same size
          background: '#F4F0FF',
          borderRadius: 12,
          padding: '8px 16px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          boxSizing: 'border-box',
          gap: 6,
          marginBottom: 16,
          cursor: 'not-allowed' // Show disabled cursor
        }}>
          <div style={{
            fontSize: 12,
            color: '#636363',
            fontFamily: 'Roboto',
            fontWeight: 400,
            lineHeight: '100%'
          }}>
            {selectedClasses.length > 1 ? 'Batches' : 'Batch'} <span style={{ color: '#D32F2F', marginLeft: 4 }}>*</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(selectedClasses || []).map((c, idx) => (
              <div key={c.id} style={{
                fontSize: 16,
                color: '#191919',
                fontFamily: 'Roboto',
                fontWeight: 400,
                lineHeight: 'normal',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {c.name}
              </div>
            ))}
          </div>
          {/* NOT IMPLEMENTED: spec calls for a 24x24 delete icon (stroke #FF414D) per
              batch row plus a right-aligned "ADD BATCH" link (Roboto Bold 16px #1089FF).
              This field is currently rendered read-only (cursor: not-allowed, same as the
              Subject field above) and onRemoveClass exists but is never wired to any UI
              element - adding real remove/add-batch affordances would mean building new
              interactive elements and possibly a batch-picker dropdown, which is
              behavior, not styling, so it's left out of this visual-only pass. */}
        </div>

        {/* Chapter Field - a class recording has no chapter of its own */}
        {!isRecording && (
          <div className="resource-field">
            {(chapterName || showChapterDropdown) && (
              <div style={{
                fontSize: 12,
                color: '#636363',
                marginBottom: 0,
                fontWeight: 400
              }}>
                Chapter <span style={{ color: '#D32F2F', marginLeft: 4 }}>*</span>
              </div>
            )}
            <div
              id="chapter-dropdown-container"
              style={{ position: 'relative' }}
              onMouseEnter={() => setIsChapterHovered(true)}
              onMouseLeave={() => setIsChapterHovered(false)}
            >
              {!chapterName && !showChapterDropdown && (
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  pointerEvents: 'none',
                  fontSize: '16px',
                  color: '#636363',
                  fontFamily: 'Roboto',
                  lineHeight: '100%',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  Chapter <span style={{ color: '#D32F2F', marginLeft: 4 }}>*</span>
                </div>
              )}
              <AutoComplete
                popupClassName="resource-add-dropdown"
                options={optionChapter}
                onSearch={handleSearchChapter}
                onSelect={onSelectChapter}
                onChange={(value) => {
                  if (!value) {
                    // Clearing the chapter (the "x", or emptying the field) shouldn't wipe
                    // out a title the user already typed — same reasoning as onSelectChapter
                    // above. Only the Title field's own clear should clear the title.
                    setChapterName('');
                    setChapterId(null);
                    setTopicId(null);
                    setTopicName('');
                    setChapterSearchText(null);
                    setTopicSearchText(null);
                  }
                }}
                onFocus={() => {
                  setShowChapterDropdown(true);
                  if (fetchAdminChapterFilter && subjectId) {
                    const classId = selectedClasses.length > 0 ? selectedClasses[0].id : null;
                    if (classId) {
                      fetchAdminChapterFilter('', classId, subjectId);
                    }
                  }
                }}
                onDropdownVisibleChange={(open) => {
                  setShowChapterDropdown(open)
                }}
                onBlur={() => {
                  setTimeout(() => setShowChapterDropdown(false), 200);
                }}
                value={chapterName}
                style={{ width: '100%' }}
                open={showChapterDropdown}
                disabled={!subjectId || selectedClasses.length === 0}
                filterOption={(inputValue, option) => {
                  // Not typing (value came from a selection, or the box is empty) -
                  // list everything rather than just the one chapter already in it.
                  if (chapterSearchText === null) return true;
                  return String(option.value || '').toUpperCase().indexOf(inputValue.toUpperCase()) !== -1;
                }}
                placement="bottomLeft"
                getPopupContainer={() => document.getElementById('chapter-dropdown-container')}
                dropdownMatchSelectWidth={true}
                notFoundContent="No chapter found"
                listHeight={200}
              >
                <Input
                  bordered={false}
                  style={{
                    padding: 0,
                    paddingRight: 24,
                    paddingTop: 0, // Reduced from 6
                    fontSize: '16px',
                    color: '#191919',
                    fontWeight: 400,
                    fontFamily: 'Roboto',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    height: 'auto',
                    minHeight: '20px'
                  }}
                />
              </AutoComplete>
              {isChapterHovered && chapterName ? (
                <div
                  onClick={() => {
                    // Same as the onChange clear above — clearing the chapter shouldn't
                    // clear the title too.
                    setChapterName('');
                    setChapterId(null);
                    setTopicId(null);
                    setTopicName('');
                    setChapterSearchText(null);
                    setTopicSearchText(null);
                  }}
                  style={{
                    width: 18,
                    height: 18,
                    cursor: 'pointer',
                    position: 'absolute',
                    right: -4,
                    top: '50%',
                    marginTop: 4,
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#636363',
                    fontSize: 16,
                    paddingBottom: 2
                  }}
                  title="Clear chapter"
                >
                  ✕
                </div>
              ) : (
                <img
                  src={magnifierIcon}
                  alt="search"
                  style={{
                    width: 24,
                    height: 24,
                    cursor: 'text',
                    position: 'absolute',
                    right: -4,
                    top: '50%',
                    marginTop: 1,
                    // Extra room below the icon's own box - translateY(-50%) centers
                    // against the box's full height (content + padding), so growing
                    // the box downward via padding, then re-centering it, nudges the
                    // icon itself up off the divider line below instead of just
                    // leaving unused space to its right.
                    paddingBottom: 6,
                    transform: 'translateY(-50%)',
                    opacity: (!subjectId || selectedClasses.length === 0) ? 0.3 : 1,
                    zIndex: 2
                  }}
                />
              )}
              <div style={{ width: '100%', height: '0px', borderTop: '1px solid #E6E6E6', opacity: 1, }} />
            </div>
          </div>
        )}

        {/* Topic Field - a class recording has no topic of its own */}
        {!isRecording && (
          <div className="resource-field">
            {(topicName || showTopicDropdown) && (
              <div style={{
                fontSize: 12,
                color: '#636363',
                marginBottom: 0,
                fontWeight: 400
              }}>
                Topic <span style={{ color: '#D32F2F', marginLeft: 4 }}>*</span>
              </div>
            )}
            <div id="topic-dropdown-container" style={{ position: 'relative' }}>
              {!topicName && !showTopicDropdown && (
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  pointerEvents: 'none',
                  fontSize: '16px',
                  color: '#636363',
                  fontFamily: 'Roboto',
                  lineHeight: '100%',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  Topic <span style={{ color: '#D32F2F', marginLeft: 4 }}>*</span>
                </div>
              )}
              <AutoComplete
                popupClassName="resource-add-dropdown"
                onSearch={handleSearchTopic}
                onSelect={onSelectTopic}
                onChange={(value) => {
                  if (value === -1 || value === '-1') {
                    setTopicName('');
                    setShowTopicError(false);
                  } else if (!value) {
                    setTopicName('');
                    setTopicId(null);
                    setShowTopicError(false);
                  } else {
                    setTopicName(value);
                    // Clear topicId on manual type to force selection from dropdown
                    setTopicId(null);
                    setShowTopicError(false);
                  }
                }}
                onFocus={() => {
                  setShowTopicDropdown(true);
                  if (fetchFilterTopic && subjectId && chapterId) {
                    const classId = selectedClasses.length > 0 ? selectedClasses[0].id : null;
                    if (classId) {
                      fetchFilterTopic(subjectId, classId, chapterId);
                    }
                  }
                }}
                onDropdownVisibleChange={(open) => {
                  setShowTopicDropdown(open)
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setShowTopicDropdown(false);
                  }, 300);
                }}
                value={topicName}
                style={{ width: '100%', cursor: !chapterId ? 'not-allowed' : 'text' }}
                open={showTopicDropdown}
                disabled={!chapterId}
                options={
                  (topicName && typeof topicName === 'string' && !optionTopic.some(o => o.label?.toString().toLowerCase() === topicName.toLowerCase()))
                    ? [{ value: -1, label: `Create topic "${topicName}"`, key: 'create-topic', topicNamePayload: topicName }, ...optionTopic]
                    : (optionTopic.length > 0 ? optionTopic : [])
                }
                filterOption={(inputValue, option) => {
                  // The synthetic "Create topic" row is never filtered out.
                  if (option.value === -1 || option.value === '-1') return true;
                  if (topicSearchText === null) return true;
                  return option.value && option.value.toString().toUpperCase().indexOf(inputValue.toUpperCase()) !== -1;
                }}
                placement="bottomLeft"
                getPopupContainer={() => document.getElementById('topic-dropdown-container')}
                dropdownMatchSelectWidth={true}
                notFoundContent="No topic found"
                listHeight={200}
              >
                <Input
                  bordered={false}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && topicName) {
                      const matchingTopic = optionTopic.find(o => o.label.toLowerCase() === topicName.toLowerCase());
                      if (!matchingTopic) {
                        e.preventDefault();
                        onSelectTopic(-1, { topicNamePayload: topicName });
                      }
                    }
                  }}
                  style={{
                    padding: 0,
                    paddingRight: 24,
                    paddingTop: 0, // Reduced from 6
                    fontSize: '16px',
                    color: '#191919',
                    fontWeight: 400,
                    fontFamily: 'Roboto',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    height: 'auto',
                    minHeight: '20px',
                    // bordered={false} drops antd's .ant-input-disabled styling, which
                    // is what normally supplies the not-allowed cursor - set it here so
                    // a Topic field locked behind an unselected chapter actually looks
                    // locked instead of offering a text caret.
                    cursor: !chapterId ? 'not-allowed' : 'text'
                  }}
                />
              </AutoComplete>
              <img
                src={showTopicDropdown ? upArrowIcon : downArrowIcon}
                alt={showTopicDropdown ? "up arrow" : "down arrow"}
                style={{
                  width: 24,
                  height: 24,
                  cursor: !chapterId ? 'not-allowed' : 'pointer',
                  position: 'absolute',
                  right: -4,
                  top: '40%', // Raised slightly
                  transform: 'translateY(-50%)',
                  opacity: !chapterId ? 0.3 : 1,
                  zIndex: 2
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (chapterId) setShowTopicDropdown(!showTopicDropdown);
                }}
              />
              <div style={{ width: '100%', height: '0px', borderTop: '1px solid #E6E6E6', opacity: 1, }} />
              {showTopicError && (
                <div style={{ color: '#D32F2F', fontSize: 11, fontWeight: 400, marginTop: 4, textAlign: 'right' }}>
                  create topic first
                </div>
              )}
            </div>
          </div>
        )}

        {/* Title Field */}
        <div className="resource-field">
          {(title || isTitleFocused) && (
            <div style={{
              fontSize: 12,
              color: '#636363',
              marginBottom: 0,
              fontWeight: 400
            }}>
              Title <span style={{ color: '#D32F2F', marginLeft: 4 }}>*</span>
            </div>
          )}
          <div style={{ position: 'relative' }}>
            {!title && !isTitleFocused && (
              <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                pointerEvents: 'none',
                fontSize: '16px',
                color: '#636363',
                fontFamily: 'Roboto',
                lineHeight: '100%',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center'
              }}>
                Title <span style={{ color: '#D32F2F', marginLeft: 4 }}>*</span>
              </div>
            )}
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onFocus={() => setIsTitleFocused(true)}
              onBlur={() => setIsTitleFocused(false)}
              bordered={false}
              style={{
                padding: 0,
                paddingTop: 0, // Reduced from 6
                fontSize: '16px',
                color: '#191919',
                fontWeight: 400,
                fontFamily: 'Roboto',
                lineHeight: '100%',
                letterSpacing: '0%',
                height: 'auto', // Changed from 31px
                minHeight: '20px'
              }}
            />
          </div>
          <div style={{ width: '100%', height: '0px', borderTop: '1px solid #E6E6E6', opacity: 1, }} />
        </div>

        </div> {/* End of upload-locked fields */}
      </div> {/* End of resource-add-content */}

      {/* Action Buttons - sticky footer */}
      <div className="resource-add-footer" style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        padding: 'var(--res-footer-padding)',
        background: '#FFFFFF',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
        flexShrink: 0,
        zIndex: 1100
      }}>
        <button
          onClick={onCancel}
          disabled={uploadProgressCheck}
          style={{
            width: 140,
            height: 'var(--res-btn-h)',
            padding: '4px 16px',
            borderRadius: 12,
            border: '1px solid #636363',
            background: '#FFFFFF',
            color: uploadProgressCheck ? '#AEAEAE' : '#636363',
            fontSize: 16,
            fontWeight: 700,
            cursor: uploadProgressCheck ? 'not-allowed' : 'pointer',
            letterSpacing: '0.5px',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          BACK
        </button>
        <button
          onClick={handleReset}
          disabled={uploadProgressCheck || !isFormDirty}
          style={{
            width: 140,
            height: 'var(--res-btn-h)',
            padding: '4px 16px',
            borderRadius: 12,
            border: '1px solid #636363',
            background: '#FFFFFF',
            color: (isFormDirty && !uploadProgressCheck) ? '#636363' : '#AEAEAE',
            fontSize: 16,
            fontWeight: 700,
            cursor: (!isFormDirty || uploadProgressCheck) ? 'not-allowed' : 'pointer',
            letterSpacing: '0.5px',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10
          }}
        >
          RESET
        </button>
        <button
          onClick={handleSaveAttempt}
          disabled={uploadProgressCheck || pageLoader || !isFormValid || (initialData && !isFormDirty)}
          style={{
            width: 140,
            height: 'var(--res-btn-h)',
            padding: '4px 16px',
            borderRadius: 12,
            border: '1px solid #594099',
            background: '#594099',
            color: '#FFFFFF',
            fontSize: 16,
            fontWeight: 700,
            cursor: (uploadProgressCheck || pageLoader || !isFormValid || (initialData && !isFormDirty)) ? 'not-allowed' : 'pointer',
            opacity: (uploadProgressCheck || pageLoader || !isFormValid || (initialData && !isFormDirty)) ? 0.7 : 1,
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10
          }}
        >
          SAVE
        </button>
      </div>

      <style>{`
        .resource-add-wrapper {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
          font-family: Roboto, sans-serif;
          position: relative;
        }

        .resource-add-content {
          flex: 1;
          overflow-y: auto !important;
          padding: var(--res-padding);
          max-width: 600px;
          margin: 0 auto;
          width: 100%;
        }

        .resource-add-footer {
          width: 100%;
        }

        .resource-field {
          margin-bottom: var(--res-margin);
        }

        :root {
          --res-margin: 54px;
          --res-padding: 32px 32px;
          --res-upload-h: 180px;
          --res-btn-h: 45px;
          --res-footer-padding: 16px 32px;
        }

        @media (max-height: 950px) {
          :root {
            --res-margin: 48px;
            --res-padding: 24px 24px;
            --res-upload-h: 150px;
            --res-btn-h: 42px;
            --res-footer-padding: 14px 24px;
          }
        }

        @media (max-height: 850px) {
          :root {
            --res-margin: 36px;
            --res-padding: 20px 20px;
            --res-upload-h: 120px;
            --res-btn-h: 40px;
            --res-footer-padding: 12px 20px;
          }
        }

        @media (max-height: 750px) {
          :root {
            --res-margin: 24px;
            --res-padding: 16px 18px;
            --res-upload-h: 100px;
            --res-btn-h: 36px;
            --res-footer-padding: 10px 18px;
          }
        }

        @media (max-height: 650px) {
          :root {
            --res-margin: 18px;
            --res-padding: 12px 16px;
            --res-upload-h: 80px;
            --res-btn-h: 32px;
            --res-footer-padding: 8px 16px;
          }
        }

        .resource-add-dropdown {
          z-index: 1050 !important;
          position: absolute !important;
          top: 100% !important;
          left: 0 !important;
          transform: none !important;
          margin-top: 30px !important;
          border-radius: 12px !important;
          overflow: hidden !important;
          /* Same edge-definition fix as the other dropdowns in this module. */
          border: 1px solid #E6E6E6 !important;
          background: #ffffff !important;
        }

        /* Fallback for Firefox */
        @-moz-document url-prefix() {
          .resource-add-dropdown {
            /* Handled globally */
          }
        }

        .pdf-type-item:hover {
          background: rgba(198, 194, 255, 0.4) !important;
        }

        .pdf-type-item:active {
          background: #C6C2FF !important;
        }

        .resource-add-dropdown .ant-select-item-option-selected,
        .resource-add-dropdown .custom-selected-option {
          background-color: #C6C2FF !important;
          color: #191919 !important;
          font-weight: 500;
        }

        .resource-add-dropdown .ant-select-item-option-active {
          background-color: rgba(198, 194, 255, 0.6) !important;
        }

        .resource-add-dropdown .ant-select-item {
          border-radius: 8px;
          margin: 4px;
        }

        .resource-add-dropdown .ant-select-item-empty {
          cursor: not-allowed !important;
        }

        .ant-input-affix-wrapper,
        .ant-input-affix-wrapper-focused,
        .ant-input-affix-wrapper:hover {
          border: none !important;
          box-shadow: none !important;
        }

        .ant-input-affix-wrapper::before,
        .ant-input-affix-wrapper::after {
          display: none !important;
        }

        .ant-input,
        .ant-input:focus,
        .ant-input:hover {
          border-bottom: none !important;
          box-shadow: none !important;
          outline: none !important;
        }

        /* While an upload is running every field below the progress bar is inert.
           The lock lives on the wrapper - not on the fields themselves - so the wrapper
           still receives hover and can paint the not-allowed cursor; the children are the
           ones taking pointer-events: none, which their descendants inherit. Doing it the
           other way round (pointer-events: none on the wrapper) makes the whole area
           invisible to the mouse and the cursor falls back to the page default. */
        .resource-fields-locked {
          cursor: not-allowed !important;
          opacity: 0.6;
        }

        .resource-fields-locked > * {
          pointer-events: none !important;
        }

        .ant-input::placeholder {
          color: #636363;
          font-family: Roboto;
          font-weight: 400;
          font-size: 16px;
          line-height: 100%;
          letter-spacing: 0%;
        }
      `}</style>

      <SavingModal
        visible={showSaveModal}
        onCancel={() => setShowSaveModal(false)}
        onConfirm={() => {
          setShowSaveModal(false);
          handleSave();
        }}
        title="Confirm"
        message={initialData ? "Are you sure you want to save the resource?" : "Are you sure you want to add the resource?"}
        confirmText="YES"
        cancelText="NO"
      />

      {storageModalVisible &&
        <AulasSubscriptionModal
          isModalVisible={storageModalVisible}
          handleCancel={() => setStorageModalVisible(false)}
          availableStorageBytes={availableStorageBytes}
          fromSchedule
        />
      }
    </div>
  );
};

export default ResourceAddForm;